import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import createVcj from "./createVcf";
import { clearIdentity, trackEvent } from "../analytics";
import { getCardTheme } from "../lib/cardTheme";
import { getCardFeatureSnapshot } from "../lib/cardFeatureTracking";
import { cardNoIndexSeo, setPageSeo } from "../seo";
import Loader from "./loader";
import QrModalCard from "./QrModalCard";
import InvoicingModalCard from "./InvoicingModalCard";

const getPhoneValue = (data, fieldName, prefixFieldName) => {
  const phone = data?.[fieldName] || "";
  const prefix = data?.[prefixFieldName] || "";

  if (!phone) {
    return "";
  }

  return phone.trim().startsWith("+") || !prefix ? phone : `${prefix}${phone}`;
};

const getWhatsAppValue = (value) => value.replace(/\D/g, "");

const getDomain = (value = "") => {
  if (!value) return "";
  try {
    return new URL(
      /^[a-z][a-z\d+.-]*:\/\//i.test(value) ? value : `https://${value}`,
    ).hostname.replace(/^www\./i, "");
  } catch {
    return "";
  }
};

const getPublicCardAnalyticsProps = (payload = {}) => {
  const data = payload.data || {};
  const analytics = payload.analytics || data.analytics || {};
  const featureSnapshot = getCardFeatureSnapshot({
    ...data,
    cardUrl: payload.url || data.cardUrl,
  });
  const featuresEnabled =
    analytics.featureFlags ||
    analytics.features_enabled ||
    featureSnapshot.featuresEnabled;

  return {
    ...(analytics.cardId || data.cardId || data.objectId || data.id
      ? { cardId: analytics.cardId || data.cardId || data.objectId || data.id }
      : {}),
    ...(analytics.groupId || data.groupId || data.companyId
      ? { groupId: analytics.groupId || data.groupId || data.companyId }
      : {}),
    ...(analytics.cardSlug || data.slug ? { cardSlug: analytics.cardSlug || data.slug } : {}),
    plan: analytics.plan || data.plan || "none",
    ...(analytics.planStatus || data.planStatus
      ? { planStatus: analytics.planStatus || data.planStatus }
      : {}),
    features_enabled: featuresEnabled,
    features_enabled_count: Array.isArray(featuresEnabled)
      ? featuresEnabled.length
      : featureSnapshot.featuresEnabledCount,
  };
};

function CardPage() {
  const [person, setPerson] = useState();
  const [loading, setLoading] = useState(true);
  const [isInvoicingModalOpen, setIsInvoicingModalOpen] = useState(false);
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false);
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [avatarLoadError, setAvatarLoadError] = useState(false);
  const [logoLoadError, setLogoLoadError] = useState(false);
  const { orgName, personFullName } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    clearIdentity();
    setPageSeo({
      ...cardNoIndexSeo,
      path: window.location.pathname,
    });
  }, []);

  useEffect(() => {
    const fetchData = async (orgName, personFullName) => {
      try {
        const data = await fetch(
          `https://${process.env.REACT_APP_CARDS_API_URL}/slug/?slug=${orgName}/${personFullName}`
        );

        const json = await data.json();
        setPerson(json);
        setLoading(false);
        trackEvent("card_viewed", {
          label: "Card viewed",
          ...getPublicCardAnalyticsProps(json),
          orgName,
          personFullName,
          companyName: json?.data?.companyName,
        });
      } catch (error) {
        console.log(error);
        trackEvent("card_fetch_failed", {
          label: "Card fetch failed",
          orgName,
          personFullName,
        });
        navigate("/");
      }
    };

    fetchData(orgName, personFullName).catch(console.error);
  }, [orgName, personFullName, navigate]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setShowStickyHeader(scrollPosition > 200);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!person?.data) return;

    const fullName = `${person.data.firstName} ${person.data.lastName}`.trim();
    const theme = getCardTheme(person.data.plan === "plus" ? person.data : {});
    const origin = window.location.origin;
    const path = `${window.location.pathname}${window.location.search}`;
    const absoluteStart = `${origin}${path}`;

    const manifest = {
      short_name: fullName || "Biglietto da visita",
      name: fullName
        ? `Biglietto da visita - ${fullName}`
        : "Biglietto da visita digitale",
      icons: [
        { src: `${origin}/ofc_logo.png`, type: "image/png", sizes: "192x192" },
        { src: `${origin}/ofc_logo.png`, type: "image/png", sizes: "512x512" },
      ],
      start_url: absoluteStart,
      scope: absoluteStart,
      id: absoluteStart,
      display: "standalone",
      orientation: "portrait",
      theme_color: theme.colors.primary,
      background_color: theme.colors.surface,
    };

    const manifestDataUrl =
      "data:application/manifest+json;charset=utf-8," +
      encodeURIComponent(JSON.stringify(manifest));

    document
      .querySelectorAll('link[rel="manifest"]')
      .forEach((node) => node.parentNode.removeChild(node));

    const link = document.createElement("link");
    link.rel = "manifest";
    link.setAttribute("href", manifestDataUrl);
    document.head.appendChild(link);

    setPageSeo({
      ...cardNoIndexSeo,
      title: fullName
        ? `${fullName} | Cards`
        : cardNoIndexSeo.title,
      description: fullName
        ? `Biglietto da visita digitale di ${fullName}.`
        : cardNoIndexSeo.description,
      path: window.location.pathname,
    });

    const appleTitle = document.querySelector(
      'meta[name="apple-mobile-web-app-title"]'
    );
    const previousAppleTitle = appleTitle?.getAttribute("content");
    if (appleTitle && fullName) {
      appleTitle.setAttribute("content", fullName);
    }

    return () => {
      if (link.parentNode) link.parentNode.removeChild(link);
      if (appleTitle && previousAppleTitle !== undefined) {
        appleTitle.setAttribute("content", previousAppleTitle);
      }
    };
  }, [person]);

  useEffect(() => {
    setAvatarLoadError(false);
    setLogoLoadError(false);
  }, [person?.data?.avatarUrl, person?.data?.logoUrl]);

  useEffect(() => {
    if (!person?.data) return undefined;

    const handleInstallPrompt = () => {
      trackEvent("card_install_prompt_shown", {
        ...getPublicCardAnalyticsProps(person),
        orgName,
        personFullName,
      });
    };
    const handleAppInstalled = () => {
      trackEvent("card_pwa_installed", {
        ...getPublicCardAnalyticsProps(person),
        orgName,
        personFullName,
      });
    };

    window.addEventListener("beforeinstallprompt", handleInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, [orgName, person, personFullName]);

  // const displayEmail = () => {
  //   const textClassName =
  //     person.data.email?.length >= 35 ? "text-extraSm" : "text-sm";

  //   return <p className={`${textClassName} truncate`}>{person.data.email}</p>;
  // };

  // const displayAddress = () => {
  //   const fullAddress = (person.data.city + " " + person.data.address).replace(
  //     /\s/g,
  //     "+"
  //   );

  //   return (
  //     <a
  //       href={`http://maps.apple.com/?q=${fullAddress}`}
  //       target="_blank"
  //       rel="noopener noreferrer"
  //       className="h-full flex items-center leading-5"
  //     >
  //       <Icon
  //         icon="carbon:location-company-filled"
  //         className="text-4xl text-ofcBlue ml-1 mr-3"
  //       />
  //       <div>
  //         <p className="text-ofcBlue font-medium">Indirizzo</p>
  //         <p>{person.data.address}</p>
  //         <p>{person.data.city}</p>
  //       </div>
  //     </a>
  //   );
  // };

  const hasPlusPlan = person?.data?.plan === "plus";
  const shouldShowInvoicing =
    hasPlusPlan &&
    Boolean(
      person?.data.billingCompanyName ||
        person?.data.vat ||
        person?.data.electronicInvoicingId ||
        person?.data.pec
    );
  const mobileNumber = getPhoneValue(person?.data, "mobile", "mobilePrefix");
  const phoneNumber = getPhoneValue(person?.data, "phone", "phonePrefix");
  const whatsAppNumber = getWhatsAppValue(mobileNumber);
  const shareUrl = `${window.location.origin}${window.location.pathname}`;
  const cardTrackingProps = person ? getPublicCardAnalyticsProps(person) : {};
  const cardsProjectUrl = `${window.location.origin}/?utm_source=cards_public&utm_medium=card_footer&utm_campaign=scopri_cards`;
  const cardsSignupUrl = `${window.location.origin}/?utm_source=cards_public&utm_medium=card_footer&utm_campaign=crea_gratis#iscriviti`;
  const hasCompanyLogo = hasPlusPlan && person?.data.logoUrl && !logoLoadError;
  const hasAvatar = person?.data.avatarUrl && !avatarLoadError;
  const websiteUrl = person?.data.website
    ? /^https?:\/\//i.test(person.data.website)
      ? person.data.website
      : `https://${person.data.website}`
    : "";
  const officeAddressQuery = [
    person?.data.address,
    person?.data.city,
  ]
    .filter(Boolean)
    .join(" ");
  const officeMapsSearchUrl = officeAddressQuery
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(officeAddressQuery)}`
    : "";
  const officeMapsDirectionsUrl = officeAddressQuery
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(officeAddressQuery)}`
    : "";
  const cardTheme = getCardTheme(hasPlusPlan ? person?.data || {} : {});
  const themeColors = cardTheme.colors;
  const themeRadius = cardTheme.radius;
  const gradientStyle = {
    background: `linear-gradient(135deg, ${themeColors.primary}, ${themeColors.secondary})`,
  };
  const themedSurfaceStyle = {
    backgroundColor: themeColors.surface,
    color: themeColors.text,
  };
  const themedPanelStyle = {
    backgroundColor: `${themeColors.background}cc`,
  };
  const themedIconStyle = {
    backgroundColor: `${themeColors.secondary}1a`,
  };

  return (
    <div className="min-h-screen font-sans" style={{ backgroundColor: themeColors.background, fontFamily: cardTheme.font.family }} onClick={(e) => {
      // Close share menu when clicking outside
      if (isShareMenuOpen && !e.target.closest('.share-menu-container')) {
        setIsShareMenuOpen(false);
      }
    }}>
      {loading && <Loader />}
      {!loading && (
        <>
          {/* Sticky Header */}
          <div className={`fixed top-0 left-0 right-0 shadow-md z-50 transition-transform duration-300 ${showStickyHeader ? 'translate-y-0' : '-translate-y-full'}`} style={gradientStyle}>
            <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 bg-white/20 flex items-center justify-center overflow-hidden ring-2 ring-white/25 ${themeRadius.avatarClassName}`}>
                  {hasAvatar ? (
                    <img
                      src={person.data.avatarUrl}
                      alt={`${person.data.firstName} ${person.data.lastName}`}
                      className="h-full w-full object-cover"
                      onError={() => setAvatarLoadError(true)}
                    />
                  ) : (
                    <Icon icon="mdi:account" className="text-xl text-white" />
                  )}
                </div>
                <div>
                  <h2 className="font-semibold text-white">
                    {person.data.firstName + " " + person.data.lastName}
                  </h2>
                  {hasPlusPlan && person.data.companyName && (
                    <p className="text-sm text-white/80">{person.data.companyName}</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => {
                  trackEvent("save_contact", {
                    label: "Salva in Rubrica - Sticky",
                    source: "sticky_header",
                    ...cardTrackingProps,
                    orgName,
                    personFullName,
                  });
                  return createVcj(person.data);
                }}
                className="px-4 py-2 bg-white/20 text-white text-sm font-medium rounded-lg hover:bg-white/30 transition-colors backdrop-blur-sm"
              >
                <Icon icon="mdi:account-plus" className="inline mr-1" />
                Salva
              </button>
            </div>
          </div>
          
          <div className="flex flex-col items-center justify-center min-h-screen p-4">
            <div className={`w-full max-w-md shadow-lg overflow-hidden ${themeRadius.cardClassName}`} style={themedSurfaceStyle}>
            {/* Header con nome e ruolo */}
            <div className="relative pt-8 pb-8 text-center" style={gradientStyle}>
              {/* Share button in top right */}
              <div className="absolute top-4 right-4 share-menu-container">
                <button
                  onClick={async () => {
                    const shareData = {
                      title: `${person.data.firstName} ${person.data.lastName}`,
                      text: `Contatta ${person.data.firstName} ${person.data.lastName}`,
                      url: shareUrl
                    };
                    
                    // Check if it's mobile device
                    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                    
                    // On mobile, use native share if available
                    if (isMobile && navigator.share) {
                      try {
                        await navigator.share(shareData);
                        trackEvent("share", {
                          label: "Native Share",
                          method: "native_mobile",
                          ...cardTrackingProps,
                          orgName,
                          personFullName,
                        });
                      } catch (err) {
                        // User cancelled sharing - do nothing
                      }
                    } else {
                      // On desktop, always show custom menu
                      const nextOpen = !isShareMenuOpen;
                      setIsShareMenuOpen(nextOpen);
                      if (nextOpen) {
                        trackEvent("share_menu_open", {
                          label: "Apri menu condivisione",
                          ...cardTrackingProps,
                          orgName,
                          personFullName,
                        });
                      }
                    }
                  }}
                  className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                >
                  <Icon icon="mdi:share-variant" className="text-xl text-white" />
                </button>
                
                {/* Share menu dropdown */}
                {isShareMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg overflow-hidden z-10">
                    <button
                      onClick={() => {
                        const shareText = `Contatta ${person.data.firstName} ${person.data.lastName}`;
                        window.open(`sms:?body=${encodeURIComponent(shareText + ' ' + shareUrl)}`);
                        setIsShareMenuOpen(false);
                        trackEvent("share", {
                          label: "SMS",
                          method: "sms",
                          ...cardTrackingProps,
                          orgName,
                          personFullName,
                        });
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-gray-100 flex items-center gap-3"
                    >
                      <Icon icon="mdi:message-text" className="text-xl text-[#1b8395]" />
                      <span className="text-gray-700">SMS</span>
                    </button>
                    
                    <button
                      onClick={() => {
                        const shareText = `Contatta ${person.data.firstName} ${person.data.lastName}`;
                        window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`);
                        setIsShareMenuOpen(false);
                        trackEvent("share", {
                          label: "WhatsApp",
                          method: "whatsapp",
                          ...cardTrackingProps,
                          orgName,
                          personFullName,
                        });
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-gray-100 flex items-center gap-3 border-t"
                    >
                      <Icon icon="mdi:whatsapp" className="text-xl text-[#25D366]" />
                      <span className="text-gray-700">WhatsApp</span>
                    </button>
                    
                    <button
                      onClick={() => {
                        const shareText = `Contatta ${person.data.firstName} ${person.data.lastName}`;
                        window.location.href = `mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`;
                        setIsShareMenuOpen(false);
                        trackEvent("share", {
                          label: "Email",
                          method: "email",
                          ...cardTrackingProps,
                          orgName,
                          personFullName,
                        });
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-gray-100 flex items-center gap-3 border-t"
                    >
                      <Icon icon="mdi:email" className="text-xl text-[#1b8395]" />
                      <span className="text-gray-700">Email</span>
                    </button>
                    
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(shareUrl);
                        setShowCopiedMessage(true);
                        setTimeout(() => setShowCopiedMessage(false), 2000);
                        setIsShareMenuOpen(false);
                        trackEvent("share", {
                          label: "Copy Link",
                          method: "copy_link",
                          ...cardTrackingProps,
                          orgName,
                          personFullName,
                        });
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-gray-100 flex items-center gap-3 border-t"
                    >
                      <Icon icon="mdi:content-copy" className="text-xl text-[#1b8395]" />
                      <span className="text-gray-700">Copia link</span>
                    </button>
                    
                    {/* Native share option for desktop */}
                    {navigator.share && (
                      <button
                        onClick={async () => {
                          const shareData = {
                            title: `${person.data.firstName} ${person.data.lastName}`,
                            text: `Contatta ${person.data.firstName} ${person.data.lastName}`,
                            url: shareUrl
                          };
                          try {
                            await navigator.share(shareData);
                            setIsShareMenuOpen(false);
                            trackEvent("share", {
                              label: "Native Share Desktop",
                              method: "native_desktop",
                              ...cardTrackingProps,
                              orgName,
                              personFullName,
                            });
                          } catch (err) {
                            // User cancelled sharing
                          }
                        }}
                        className="w-full px-4 py-3 text-left hover:bg-gray-100 flex items-center gap-3 border-t"
                      >
                        <Icon icon="mdi:share-all" className="text-xl text-[#1b8395]" />
                        <span className="text-gray-700">Altro...</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
              
              {/* Copied message */}
              {showCopiedMessage && (
                <div className="absolute top-16 right-4 bg-black/80 text-white px-3 py-2 rounded-lg text-sm">
                  Link copiato!
                </div>
              )}
              <div className={`w-16 h-16 mx-auto mb-3 bg-white/20 flex items-center justify-center overflow-hidden ring-2 ring-white/25 ${themeRadius.avatarClassName}`}>
                {hasAvatar ? (
                  <img
                    src={person.data.avatarUrl}
                    alt={`${person.data.firstName} ${person.data.lastName}`}
                    className="h-full w-full object-cover"
                    onError={() => setAvatarLoadError(true)}
                  />
                ) : (
                  <Icon icon="mdi:account" className="text-3xl text-white" />
                )}
              </div>
              <h1 className="text-2xl font-bold text-white mb-1">
                {person.data.firstName + " " + person.data.lastName}
              </h1>
              {hasPlusPlan && person.data.role && (
                <p className="text-sm font-semibold text-cyan-50 mb-2">
                  {person.data.role}
                </p>
              )}
              {hasPlusPlan && person.data.companyName && (
                <p className="text-white/90 font-medium flex items-center justify-center mb-4">
                  <Icon icon="mdi:office-building" className="text-sm mr-1" />
                  {person.data.companyName}
                </p>
              )}
              {/* Social icons */}
              {hasPlusPlan && (person.data.website || person.data.linkedin || person.data.facebook || person.data.instagram || person.data.twitter || person.data.youtube || person.data.tiktok) && (
                <div className="flex justify-center gap-3">
                  {person.data.website && (
                    <a href={websiteUrl} target="_blank" rel="noreferrer"
                       onClick={() => trackEvent("website_click", { label: "Sito web", source: "header_icon", domain: getDomain(person.data.website), ...cardTrackingProps, orgName, personFullName })}
                       className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors">
                      <Icon icon="mdi:web" className="text-base text-white" />
                    </a>
                  )}
                  {person.data.linkedin && (
                    <a href={person.data.linkedin} target="_blank" rel="noreferrer"
                       onClick={() => trackEvent("social_click", { label: "LinkedIn", network: "linkedin", domain: getDomain(person.data.linkedin), ...cardTrackingProps, orgName, personFullName })}
                       className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors">
                      <Icon icon="mdi:linkedin" className="text-base text-white" />
                    </a>
                  )}
                  {person.data.facebook && (
                    <a href={person.data.facebook} target="_blank" rel="noreferrer"
                       onClick={() => trackEvent("social_click", { label: "Facebook", network: "facebook", domain: getDomain(person.data.facebook), ...cardTrackingProps, orgName, personFullName })}
                       className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors">
                      <Icon icon="mdi:facebook" className="text-base text-white" />
                    </a>
                  )}
                  {person.data.instagram && (
                    <a href={person.data.instagram} target="_blank" rel="noreferrer"
                       onClick={() => trackEvent("social_click", { label: "Instagram", network: "instagram", domain: getDomain(person.data.instagram), ...cardTrackingProps, orgName, personFullName })}
                       className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors">
                      <Icon icon="mdi:instagram" className="text-base text-white" />
                    </a>
                  )}
                  {person.data.twitter && (
                    <a href={person.data.twitter} target="_blank" rel="noreferrer"
                       onClick={() => trackEvent("social_click", { label: "Twitter", network: "twitter", domain: getDomain(person.data.twitter), ...cardTrackingProps, orgName, personFullName })}
                       className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors">
                      <Icon icon="mdi:twitter" className="text-base text-white" />
                    </a>
                  )}
                  {person.data.youtube && (
                    <a href={person.data.youtube} target="_blank" rel="noreferrer"
                       onClick={() => trackEvent("social_click", { label: "YouTube", network: "youtube", domain: getDomain(person.data.youtube), ...cardTrackingProps, orgName, personFullName })}
                       className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors">
                      <Icon icon="mdi:youtube" className="text-base text-white" />
                    </a>
                  )}
                  {person.data.tiktok && (
                    <a href={person.data.tiktok} target="_blank" rel="noreferrer"
                       onClick={() => trackEvent("social_click", { label: "TikTok", network: "tiktok", domain: getDomain(person.data.tiktok), ...cardTrackingProps, orgName, personFullName })}
                       className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors">
                      <Icon icon="mdi:music-note" className="text-base text-white" />
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Contenuto principale */}
            <div className="px-6 pt-6">

              {/* Call to action principale con pulsanti aggiuntivi */}
              <div className="flex gap-3 mb-6">
                <button
                  onClick={() => {
                    trackEvent("save_contact", {
                      label: "Salva in Rubrica",
                      source: "main_cta",
                      ...cardTrackingProps,
                      orgName,
                      personFullName,
                    });
                    return createVcj(person.data);
                  }}
                  className={`flex-1 py-4 text-white font-semibold transition-colors shadow-md ${themeRadius.buttonClassName}`}
                  style={{ backgroundColor: themeColors.secondary }}
                >
                  <Icon icon="mdi:account-plus" className="inline mr-2 text-xl" />
                  Aggiungi ai contatti
                </button>
                <div className="flex gap-3">
                  {hasPlusPlan && shouldShowInvoicing && (
                    <button
                      onClick={() => {
                        setIsInvoicingModalOpen(true);
                        trackEvent("invoicing_modal_open", {
                          label: "Modal Fatturazione",
                          ...cardTrackingProps,
                          orgName,
                          personFullName,
                        });
                      }}
                      className={`w-12 h-full transition-colors flex items-center justify-center ${themeRadius.buttonClassName}`}
                      style={{ backgroundColor: themeColors.background }}
                    >
                      <Icon
                        className="text-xl"
                        style={{ color: themeColors.primary }}
                        icon="ant-design:euro-outlined"
                      />
                    </button>
                  )}
                  {hasPlusPlan && (
                    <button
                      aria-label="qrcode-button"
                      onClick={() => {
                        setIsQrModalOpen(true);
                        trackEvent("qr_modal_open", {
                          label: "Modal QR",
                          ...cardTrackingProps,
                          orgName,
                          personFullName,
                        });
                      }}
                      className={`w-12 h-full transition-colors flex items-center justify-center ${themeRadius.buttonClassName}`}
                      style={{ backgroundColor: themeColors.background }}
                    >
                      <Icon className="text-xl" style={{ color: themeColors.primary }} icon="ion:qr-code" />
                    </button>
                  )}
                </div>
              </div>

              {/* Titolo sezione contatti personali */}
              <h3 className="text-lg font-semibold mb-4" style={{ color: themeColors.primary }}>Contatti Personali</h3>

              {/* Box contatti personali */}
              <div className="space-y-3 mb-8">
                {/* Cellulare */}
                {person.data.mobile && (
                  <a
                    href={`tel:${mobileNumber}`}
                    onClick={() => {
                      trackEvent("contact_click", {
                        label: "Cellulare",
                        channel: "mobile",
                        ...cardTrackingProps,
                        orgName,
                        personFullName,
                      });
                    }}
                    className={`flex items-center p-4 transition-colors ${themeRadius.panelClassName}`}
                    style={themedPanelStyle}
                  >
                    <div className={`p-2 mr-4 ${themeRadius.iconClassName}`} style={themedIconStyle}>
                      <Icon icon="mdi:cellphone" className="text-2xl" style={{ color: themeColors.secondary }} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Cellulare</p>
                      <p className="font-medium text-gray-900">{person.data.mobile}</p>
                    </div>
                    <Icon icon="mdi:chevron-right" className="text-gray-400 text-xl" />
                  </a>
                )}

                {/* Email */}
                {person.data.email && (
                  <a
                    href={`mailto:${person.data.email}`}
                    onClick={() => {
                      trackEvent("contact_click", {
                        label: "Email",
                        channel: "email",
                        ...cardTrackingProps,
                        orgName,
                        personFullName,
                      });
                    }}
                    className={`flex items-center p-4 transition-colors ${themeRadius.panelClassName}`}
                    style={themedPanelStyle}
                  >
                    <div className={`p-2 mr-4 ${themeRadius.iconClassName}`} style={themedIconStyle}>
                      <Icon icon="mdi:email" className="text-2xl" style={{ color: themeColors.secondary }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium text-gray-900 truncate">{person.data.email}</p>
                    </div>
                    <Icon icon="mdi:chevron-right" className="text-gray-400 text-xl" />
                  </a>
                )}

                {/* WhatsApp */}
                {person.data.mobile && whatsAppNumber && (
                  <a
                    href={`https://wa.me/${whatsAppNumber}`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => {
                      trackEvent("contact_click", {
                        label: "WhatsApp",
                        channel: "whatsapp",
                        ...cardTrackingProps,
                        orgName,
                        personFullName,
                      });
                    }}
                    className={`flex items-center p-4 transition-colors ${themeRadius.panelClassName}`}
                    style={themedPanelStyle}
                  >
                    <div className={`p-2 mr-4 ${themeRadius.iconClassName}`} style={themedIconStyle}>
                      <Icon icon="mdi:whatsapp" className="text-2xl" style={{ color: themeColors.secondary }} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">WhatsApp</p>
                      <p className="font-medium text-gray-900">Chatta su WhatsApp</p>
                    </div>
                    <Icon icon="mdi:chevron-right" className="text-gray-400 text-xl" />
                  </a>
                )}
              </div>

              {/* Pulsante chiama personale */}
              {person.data.mobile && (
                <a
                  href={`tel:${mobileNumber}`}
                  onClick={() => {
                    trackEvent("contact_click", {
                      label: "Chiama",
                      channel: "mobile",
                      source: "personal_contacts_call_cta",
                      ...cardTrackingProps,
                      orgName,
                      personFullName,
                    });
                  }}
                  className={`mb-8 block w-full py-4 text-center font-semibold text-black shadow-md transition-colors ${themeRadius.buttonClassName}`}
                  style={{ backgroundColor: themeColors.accent }}
                >
                  <Icon icon="mdi:phone" className="inline mr-2 text-xl" />
                  Chiama ora
                </a>
              )}

              {/* Titolo sezione contatti aziendali */}
              {hasPlusPlan && (hasCompanyLogo || person.data.description || person.data.companyEmail || person.data.phone || person.data.website || person.data.address || person.data.city) && (
                <>
                  <h3 className="text-lg font-semibold mb-4" style={{ color: themeColors.primary }}>Contatti Aziendali</h3>

                  {/* Box contatti aziendali */}
                  <div className="space-y-3 mb-8">
                    {/* Logo azienda */}
                    {hasCompanyLogo && (
                      <div className={`border border-gray-100 bg-white p-4 shadow-sm ${themeRadius.panelClassName}`}>
                        <img
                          src={person.data.logoUrl}
                          alt={`Logo ${person.data.companyName || ""}`.trim()}
                          className="mx-auto max-h-20 max-w-[220px] object-contain"
                          onError={() => setLogoLoadError(true)}
                        />
                      </div>
                    )}

                    {/* Descrizione aziendale */}
                    {person.data.description && (
                      <div className={`p-4 ${themeRadius.panelClassName}`} style={themedPanelStyle}>
                        <p className="text-sm font-semibold text-gray-500">
                          Descrizione
                        </p>
                        <p className="mt-1 max-h-24 overflow-hidden text-sm leading-6 text-gray-700">
                          {person.data.description}
                        </p>
                      </div>
                    )}

                    {/* Email ufficio */}
                    {person.data.companyEmail && (
                      <a
                        href={`mailto:${person.data.companyEmail}`}
                        onClick={() => {
                          trackEvent("contact_click", {
                            label: "Email ufficio",
                            channel: "company_email",
                            ...cardTrackingProps,
                            orgName,
                            personFullName,
                          });
                        }}
                        className={`flex items-center p-4 transition-colors ${themeRadius.panelClassName}`}
                        style={themedPanelStyle}
                      >
                        <div className={`p-2 mr-4 ${themeRadius.iconClassName}`} style={themedIconStyle}>
                          <Icon icon="mdi:email-outline" className="text-2xl" style={{ color: themeColors.secondary }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-500">Email ufficio</p>
                          <p className="font-medium text-gray-900 truncate">{person.data.companyEmail}</p>
                        </div>
                        <Icon icon="mdi:chevron-right" className="text-gray-400 text-xl" />
                      </a>
                    )}

                    {/* Telefono */}
                    {person.data.phone && (
                      <a
                        href={`tel:${phoneNumber}`}
                        onClick={() => {
                          trackEvent("contact_click", {
                            label: "Telefono",
                            channel: "phone",
                            ...cardTrackingProps,
                            orgName,
                            personFullName,
                          });
                        }}
                        className={`flex items-center p-4 transition-colors ${themeRadius.panelClassName}`}
                        style={themedPanelStyle}
                      >
                        <div className={`p-2 mr-4 ${themeRadius.iconClassName}`} style={themedIconStyle}>
                          <Icon icon="ph:phone-fill" className="text-2xl" style={{ color: themeColors.secondary }} />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-500">Telefono ufficio</p>
                          <p className="font-medium text-gray-900">{person.data.phone}</p>
                        </div>
                        <Icon icon="mdi:chevron-right" className="text-gray-400 text-xl" />
                      </a>
                    )}

                    {/* Sito web */}
                    {person.data.website && (
                      <a
                        href={
                          `${websiteUrl}?utm_source=cards_onoranzefunebricloud&utm_medium=digital_card&utm_campaign=card_visit`
                        }
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => {
                          trackEvent("website_click", {
                            label: "Sito web",
                            domain: getDomain(person.data.website),
                            ...cardTrackingProps,
                            orgName,
                            personFullName,
                          });
                        }}
                        className={`flex items-center p-4 transition-colors ${themeRadius.panelClassName}`}
                        style={themedPanelStyle}
                      >
                        <div className={`p-2 mr-4 ${themeRadius.iconClassName}`} style={themedIconStyle}>
                          <Icon icon="mdi:web" className="text-2xl" style={{ color: themeColors.secondary }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-500">Sito web</p>
                          <p className="font-medium text-gray-900 truncate">
                            {person.data.website
                              ?.replace(/^(?:https?:\/\/)?(?:www\.)?/i, "")
                              .split("/")[0]}
                          </p>
                        </div>
                        <Icon icon="mdi:chevron-right" className="text-gray-400 text-xl" />
                      </a>
                    )}

                    {/* Video di Presentazione */}
                    {person.data.videoUrl && (
                      <div className={`overflow-hidden ${themeRadius.panelClassName}`} style={themedPanelStyle}>
                        <div className="relative h-48 bg-gray-200">
                          <iframe
                            title="Video di Presentazione"
                            src={person.data.videoUrl.includes('youtube.com') || person.data.videoUrl.includes('youtu.be') 
                              ? person.data.videoUrl.replace('youtu.be/', 'youtube.com/embed/').replace('watch?v=', 'embed/')
                              : person.data.videoUrl}
                            className="w-full h-full"
                            style={{ border: 0 }}
                            allowFullScreen=""
                            loading="lazy"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          ></iframe>
                        </div>
                        <a
                          href={person.data.videoUrl}
                          target="_blank"
                          rel="noreferrer"
                          onClick={() => {
                            trackEvent("video_click", {
                              label: "Video di Presentazione",
                              domain: getDomain(person.data.videoUrl),
                              hasCustomTitle: Boolean(person.data.videoTitle),
                              ...cardTrackingProps,
                              orgName,
                              personFullName,
                            });
                          }}
                          className="flex items-center p-4 transition-colors"
                        >
                          <div className={`p-2 mr-4 ${themeRadius.iconClassName}`} style={themedIconStyle}>
                            <Icon icon="mdi:play-circle" className="text-2xl" style={{ color: themeColors.secondary }} />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm text-gray-500">Video di Presentazione</p>
                            <p className="font-medium text-gray-900">{person.data.videoTitle || "Guarda il video aziendale"}</p>
                          </div>
                          <Icon icon="mdi:chevron-right" className="text-gray-400 text-xl" />
                        </a>
                      </div>
                    )}

                    {/* Indirizzo con mappa */}
                    {(person.data.city || person.data.address) && (
                      <div className={`overflow-hidden ${themeRadius.panelClassName}`} style={themedPanelStyle}>
                        <a
                          href={officeMapsSearchUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => {
                            trackEvent("map_click", {
                              label: "Indirizzo",
                              ...cardTrackingProps,
                              orgName,
                              personFullName,
                            });
                          }}
                          className="block"
                        >
                          <div className="relative h-32 bg-gray-200">
                            <iframe
                              title="Mappa indirizzo"
                              src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(officeAddressQuery)}&zoom=15`}
                              className="w-full h-full"
                              style={{ border: 0 }}
                              allowFullScreen=""
                              loading="lazy"
                              referrerPolicy="no-referrer-when-downgrade"
                            ></iframe>
                          </div>
                        </a>
                        <div className="flex items-center gap-3 p-4">
                          <a
                            href={officeMapsSearchUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                              trackEvent("map_click", {
                                label: "Indirizzo",
                                ...cardTrackingProps,
                                orgName,
                                personFullName,
                              });
                            }}
                            className={`flex min-w-0 flex-1 items-center transition-colors ${themeRadius.iconClassName}`}
                          >
                            <div className={`mr-4 p-2 ${themeRadius.iconClassName}`} style={themedIconStyle}>
                              <Icon icon="mdi:map-marker" className="text-2xl" style={{ color: themeColors.secondary }} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-gray-500">Indirizzo</p>
                              <p className="font-medium text-gray-900">{person.data.address}</p>
                              {person.data.city && (
                                <p className="text-sm text-gray-600">{person.data.city}</p>
                              )}
                            </div>
                            <Icon icon="mdi:chevron-right" className="text-gray-400 text-xl" />
                          </a>
                          <a
                            href={officeMapsDirectionsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                              trackEvent("map_navigation_click", {
                                label: "Naviga",
                                ...cardTrackingProps,
                                orgName,
                                personFullName,
                              });
                            }}
                            className={`inline-flex shrink-0 items-center justify-center gap-1 px-3 py-2 text-xs font-extrabold text-white transition-colors ${themeRadius.iconClassName}`}
                            style={{ backgroundColor: themeColors.primary }}
                          >
                            <Icon icon="mdi:navigation-variant-outline" className="text-base" />
                            Naviga
                          </a>
                        </div>
                      </div>
                    )}


                    {/* Documenti e Brochure */}
                    {person.data.documents && person.data.documents.length > 0 && (
                      <div className="space-y-3">
                        {person.data.documents.map((doc, index) => (
                          <a
                            key={index}
                            href={doc.url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => {
                              trackEvent("document_click", {
                                label: "Documento",
                                docType: doc.type || "document",
                                hasCustomTitle: Boolean(doc.title),
                                domain: getDomain(doc.url),
                                ...cardTrackingProps,
                                orgName,
                                personFullName,
                              });
                            }}
                            className={`flex items-center p-4 transition-colors ${themeRadius.panelClassName}`}
                            style={themedPanelStyle}
                          >
                            <div className={`p-2 mr-4 ${themeRadius.iconClassName}`} style={themedIconStyle}>
                              <Icon 
                                icon={doc.type === 'presentation' ? 'mdi:presentation' : 
                                      doc.type === 'pdf' ? 'mdi:file-pdf-box' : 
                                      'mdi:file-document-multiple'} 
                                className="text-2xl"
                                style={{ color: themeColors.secondary }}
                              />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm text-gray-500">
                                {doc.type === 'brochure' ? 'Brochure' : 
                                 doc.type === 'presentation' ? 'Presentazione' : 
                                 'Documento'}
                              </p>
                              <p className="font-medium text-gray-900">{doc.title || "Scarica documento"}</p>
                            </div>
                            <Icon icon="mdi:download" className="text-gray-400 text-xl" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

            </div>

            {/* Footer */}
            <div className="px-6 py-4" style={{ backgroundColor: themeColors.primary }}>
              <div className={`mb-5 bg-white/10 p-4 text-center ring-1 ring-white/15 ${themeRadius.panelClassName}`}>
                <p className="text-sm font-bold text-white">
                  Ti piace questa Cards?
                </p>
                <p className="mx-auto mt-1 max-w-xs text-xs leading-5 text-white/75">
                  Crea gratis il tuo biglietto digitale!
                </p>
                <div className="mt-4 grid gap-2 sm:grid-cols-2">
                  <a
                    href={cardsProjectUrl}
                    onClick={() =>
                      trackEvent("project_discover_click", {
                        label: "Scopri Cards by OFC",
                        source: "card_footer",
                        ...cardTrackingProps,
                        orgName,
                        personFullName,
                      })
                    }
                    className={`inline-flex items-center justify-center gap-2 bg-white px-3 py-3 text-xs font-extrabold transition-colors hover:bg-cyan-50 ${themeRadius.iconClassName}`}
                    style={{ color: themeColors.primary }}
                  >
                    <Icon icon="mdi:cards-outline" className="text-base" />
                    Scopri Cards
                  </a>
                  <a
                    href={cardsSignupUrl}
                    onClick={() =>
                      trackEvent("project_signup_click", {
                        label: "Crea la tua Cards gratis",
                        source: "card_footer",
                        ...cardTrackingProps,
                        orgName,
                        personFullName,
                      })
                    }
                    className={`inline-flex items-center justify-center gap-2 bg-white/10 px-3 py-3 text-xs font-extrabold text-white ring-1 ring-white/20 transition-colors hover:bg-white/15 ${themeRadius.iconClassName}`}
                  >
                    <Icon icon="mdi:account-plus-outline" className="text-base" />
                    Creala gratis
                  </a>
                </div>
              </div>
              <p className="text-xs text-white/80 text-center mb-3">Card digitale erogata da</p>
              <div className="flex items-center justify-center">
                <a
                  href="https://onoranzefunebricloud.com/?utm_source=cards_onoranzefunebricloud&utm_medium=digital_card&utm_campaign=footer_logo"
                  target="_blank"
                  rel="noreferrer"
                  className="opacity-80 hover:opacity-100 transition-opacity"
                  onClick={() => {
                    trackEvent("card_footer_logo_click", {
                      ...cardTrackingProps,
                      orgName,
                      personFullName,
                    });
                  }}
                >
                  <img
                    width="40"
                    height="40"
                    src="https://cdn.onoranzefunebricloud.com/logos/2022/ofc_logo_rounded_white_100.png"
                    alt="Logo Onoranzefunebricloud"
                    className="rounded-full brightness-0 invert"
                  />
                </a>
              </div>
              <button
                type="button"
                onClick={() => {
                  trackEvent("manage_card_open", {
                    label: "Gestisci questa card",
                    source: "card_footer",
                    ...cardTrackingProps,
                    orgName,
                    personFullName,
                  });
                  window.sessionStorage.removeItem("cardsSelfServiceToken");
                  navigate("/gestisci", {
                    state: {
                      initialEmail: person?.data?.email || "",
                      sourcePath: window.location.pathname,
                    },
                  });
                }}
                className="mx-auto mt-5 flex w-fit items-center justify-center gap-2 rounded-md px-3 py-2 text-xs font-semibold text-white/75 transition-colors hover:bg-white/10 hover:text-white"
              >
                <Icon icon="mdi:pencil-outline" className="text-base" />
                Gestisci questa card
              </button>
            </div>
          </div>
          </div>
        </>
      )}
      <QrModalCard
        isQrModalOpen={isQrModalOpen}
        setIsQrModalOpen={setIsQrModalOpen}
        onTrack={(name, props = {}) =>
          trackEvent(name, {
            ...cardTrackingProps,
            orgName,
            personFullName,
            ...props,
          })
        }
      />
      {shouldShowInvoicing && (
        <InvoicingModalCard
          isInvoicingModalOpen={isInvoicingModalOpen}
          setIsInvoicingModalOpen={setIsInvoicingModalOpen}
          person={person.data}
          onTrack={(name, props = {}) =>
            trackEvent(name, {
              ...cardTrackingProps,
              orgName,
              personFullName,
              ...props,
            })
          }
        />
      )}
    </div>
  );
}

export default CardPage;
